import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import { defaultComparisonPayload } from './comparison-layout.util';
import { defaultQuotationPayload } from './quotation-layout.util';
import {
  customerDashboardBannerFieldsFromConfig,
  parseCustomerDashboardBannerModeDrafts,
} from './customer-dashboard-banner.util';
import { DEFAULT_PRODUCTS_PAGE_BANNER } from './products-page-layout.util';

export type LayoutStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export type AssignmentType = 'GENERAL' | 'COUNTRY' | 'BUSINESS_PARTNER';

export interface LayoutAssignment {
  id: string;
  layout_display: string;
  layout_display_name?: MlTextRecord | string;
  layout_display_status?: LayoutStatus;
  component_key: string;
  assignment_type: AssignmentType;
  target_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateLayoutAssignmentRequest {
  assignment_type: AssignmentType;
  target_id: string;
}

/** GET /AD/assignments/check/ query params. */
export interface AssignmentCheckParams {
  component: string;
  assignment_type: AssignmentType;
  layout_display_id: string;
  target_id?: string;
}

export type AssignmentCheckAction = 'CREATE_ASSIGNMENT' | 'MOVE_ASSIGNMENT' | 'UNCHANGED';

export interface AssignmentCheckOwner {
  layout_display_id: string;
  layout_display_name?: MlTextRecord | Record<string, string> | string;
}

export interface AssignmentCheckResponse {
  action_if_confirmed: AssignmentCheckAction;
  current_owner?: AssignmentCheckOwner | null;
}

/** POST /AD/assignments/ body. */
export interface CreateAssignmentRequest {
  layout_display_id: string;
  assignment_type: AssignmentType;
  target_id?: string;
}

export type AssignmentOperation = 'created' | 'reassigned' | 'unchanged';

export interface CreateAssignmentResponse {
  assignment: LayoutAssignment;
  operation: AssignmentOperation;
  replaced: AssignmentCheckOwner | null;
}

/** UI queue item for check → confirm → assign flow. */
export interface PendingLayoutAssignment {
  assignment_type: AssignmentType;
  target_id: string;
  label: string;
}

export interface UnassignLayoutQuery {
  assignment_type: AssignmentType;
  country?: string;
  business_partner?: string;
}

/** GET /AD/assignments/ query filters. */
export interface AssignmentListQuery {
  component?: string;
  assignment_type?: AssignmentType;
  layout_display_id?: string;
  business_partner?: string;
  bp?: string;
}

export type PortalComponentKey =
  | 'orders'
  | 'orderLine'
  | 'shipments'
  | 'shipmentLine'
  | 'CpInvoiceList'
  | 'homepage'
  | 'our-products'
  | 'product-cards'
  | 'product-details'
  | 'category'
  | 'favorite-list'
  | 'comparison'
  | 'quotation'
  | 'filter'
  | 'shopping-basket'
  | 'products'
  | 'dashboard'
  | 'app-ad-dashbord';

export interface LayoutDisplay {
  id: string;
  component_key: string;
  name: MlTextRecord | string;
  description?: MlTextRecord | string;
  status: LayoutStatus;
  payload: Record<string, unknown>;
  assignment_summary: string[];
  bp_count?: number;
  country_count?: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface LayoutsByComponentResponse {
  component_key: string;
  count: number;
  displays: LayoutDisplay[];
}

/** Human-readable labels for hub cards and configuration page titles. */
export const COMPONENT_KEY_LABELS: Record<PortalComponentKey, string> = {
  homepage: 'Home page',
  'our-products': 'Our products',
  'product-cards': 'Product cards',
  category: 'Category',
  'favorite-list': 'Favorite list',
  comparison: 'Comparison',
  quotation: 'Quotation',
  orders: 'Orders',
  orderLine: 'Orders Line',
  shipments: 'Shipments',
  shipmentLine: 'Shipments Line',
  CpInvoiceList: 'Invoices',
  'product-details': 'Product details',
  filter: 'Filter',
  'shopping-basket': 'Shopping basket',
  products: 'Products page',
  dashboard: 'Dashboard page',
  'app-ad-dashbord': 'Customer Dashboard',
};

export const PORTAL_COMPONENT_KEYS: PortalComponentKey[] = [
  'homepage',
  'our-products',
  'product-cards',
  'category',
  'favorite-list',
  'comparison',
  'quotation',
  'orders',
  'orderLine',
  'shipments',
  'shipmentLine',
  'CpInvoiceList',
  'product-details',
  'filter',
  'shopping-basket',
  'products',
  'dashboard',
  'app-ad-dashbord',
];

export function isPortalComponentKey(value: string): value is PortalComponentKey {
  return (PORTAL_COMPONENT_KEYS as string[]).includes(value);
}

/** Portal layouts that only support GENERAL + COUNTRY assignments (API rejects BUSINESS_PARTNER). */
export const LAYOUT_COMPONENTS_WITHOUT_BP_SCOPE: PortalComponentKey[] = [
  'homepage',
  'our-products',
];

export function supportsLayoutBusinessPartnerScope(
  componentKey: PortalComponentKey | string | null | undefined,
): boolean {
  return (
    !!componentKey &&
    !LAYOUT_COMPONENTS_WITHOUT_BP_SCOPE.includes(componentKey as PortalComponentKey)
  );
}

function readScalarLayoutId(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return readScalarLayoutId((value as Record<string, unknown>)['id']);
  }
  return '';
}

/**
 * Layout display id from resolve or assignment payloads.
 * Backends send this as `layout_display_id` or `layout_display` (sometimes nested).
 */
export function readLayoutDisplayId(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return readScalarLayoutId(value);
  }
  const record = value as Record<string, unknown>;
  return (
    readScalarLayoutId(record['layout_display_id']) ||
    readScalarLayoutId(record['layoutDisplayId']) ||
    readScalarLayoutId(record['display_id']) ||
    readScalarLayoutId(record['displayId']) ||
    readScalarLayoutId(record['layout_display'])
  );
}

export type LayoutResolvedVia = 'BUSINESS_PARTNER' | 'COUNTRY' | 'GENERAL';

/** One component entry from resolve / resolve-batch APIs. */
export interface ResolvedPortalLayout {
  layout_display_id: string;
  component_key: PortalComponentKey | string;
  layout_name: MlTextRecord | Record<string, string> | string;
  resolved_via: LayoutResolvedVia;
  payload: Record<string, unknown>;
}

export type ResolveBatchResponse = Partial<Record<PortalComponentKey, ResolvedPortalLayout>>;

export interface ResolveBatchRequest {
  components: PortalComponentKey[];
  business_partner_id?: string;
  country_code?: string;
}

export interface ResolveSingleParams {
  component: PortalComponentKey;
  bp?: string;
  /** ISO alpha-2 code. Sent as `country_code` (homepage resolve requires it). */
  country?: string;
  /** When set, backend may return the assigned layout document (same as admin editor). */
  layout_display_id?: string;
}

/** Portal runtime: load layouts after login (not for pure admin dashboard users). */
export function shouldLoadPortalLayoutsForUser(userGroups?: string[] | null): boolean {
  if (!userGroups?.length) {
    return false;
  }
  return !userGroups.includes('admin');
}

/** Legacy screen editors (customize payload). */
export const COMPONENT_EDITOR_ROUTES: Partial<Record<PortalComponentKey, string>> = {
  homepage: '/admin/ad/homepage',
  'our-products': '/admin/ad/our-products',
  'product-cards': '/admin/ad/product-cards',
  category: '/admin/ad/category',
  'favorite-list': '/admin/ad/favorite-list',
  comparison: '/admin/ad/comparison',
  quotation: '/admin/ad/quotation',
  orders: '/admin/ad/orders',
  orderLine: '/admin/ad/orderLine',
  shipments: '/admin/ad/shipments',
  shipmentLine: '/admin/ad/shipments',
  CpInvoiceList: '/admin/ad/CpInvoiceList',
  'product-details': '/admin/ad/product-details',
  filter: '/admin/ad/filter',
  'shopping-basket': '/admin/ad/shopping-basket',
  products: '/admin/ad/products',
  dashboard: '/admin/ad/dashboard',
  'app-ad-dashbord': '/admin/ad/customer-dashboard',
};

/** POST /AD/layouts/ request body. */
export interface CreateLayoutRequest {
  component_key: PortalComponentKey | string;
  name: Record<string, string> | string;
  description?: Record<string, string> | string;
  status: LayoutStatus;
  payload: Record<string, unknown>;
  assign_as_general?: boolean;
  countries?: string[];
  business_partners?: string[];
  /** Alias accepted by API when `business_partners` is omitted. */
  bps?: string[];
}

/** PUT /AD/layouts/{display_id}/ — send only fields to change; `payload` fully replaces stored payload. */
export interface UpdateLayoutRequest {
  name?: Record<string, string> | string;
  description?: Record<string, string> | string;
  status?: LayoutStatus;
  payload?: Record<string, unknown>;
}

const TABLE_COMPONENT_KEYS: PortalComponentKey[] = [
  'orders',
  'orderLine',
  'shipments',
  'shipmentLine',
  'CpInvoiceList',
];

/** Minimal v1-shaped payload for a new layout before the screen editor runs. */
export function defaultLayoutPayload(componentKey: PortalComponentKey): Record<string, unknown> {
  if (TABLE_COMPONENT_KEYS.includes(componentKey)) {
    return {
      columnOrder: {},
      action: { nameButton: '', hidden: false },
    };
  }
  if (componentKey === 'homepage') {
    return { sections: [] };
  }
  if (componentKey === 'our-products') {
    return {
      allProductsViewPorts: {
        verticalCard: true,
        horizontalCard: true,
        tableView: true,
        order: ['verticalCard', 'horizontalCard', 'tableView'],
        default: 'verticalCard',
      },
    };
  }
  if (componentKey === 'product-cards') {
    return { activeCardType: 'vertical', layouts: {} };
  }
  if (componentKey === 'product-details') {
    return {
      header: [],
      sections: [],
      layoutConfig: {},
      headerMobile: [],
      sectionsMobile: [],
      layoutConfigMobile: {},
    };
  }
  if (componentKey === 'category' || componentKey === 'favorite-list') {
    const viewPorts = {
      verticalCard: true,
      horizontalCard: true,
      tableView: true,
      order: ['verticalCard', 'horizontalCard', 'tableView'],
      default: 'verticalCard',
    };
    return {
      pageName: '',
      pageNameMobile: '',
      allProductsViewPorts: { ...viewPorts, order: [...viewPorts.order] },
      allProductsViewPortsMobile: { ...viewPorts, order: [...viewPorts.order] },
    };
  }
  if (componentKey === 'comparison') {
    return defaultComparisonPayload();
  }
  if (componentKey === 'quotation') {
    return defaultQuotationPayload();
  }
  if (componentKey === 'filter') {
    return {
      filterEnabled: false,
      filterVisible: true,
      priceRangeEnabled: true,
      priceRangeVisible: true,
    };
  }
  if (componentKey === 'shopping-basket') {
    return {
      pageName: {},
      desktop: {
        createBasketVisible: true,
        downloadUploadExcelVisible: true,
        shipmentAddressVisible: true,
        breakValueVisible: true,
        deliveryDateVisible: true,
        confirmationDateVisible: true,
        contactSupportVisible: true,
        basketSummaryVisible: true,
        orderInformationVisible: true,
        transportNotificationBannerVisible: true,
        itemOptionMoveToAnotherBasketVisible: true,
        itemOptionScheduleDeliveryDateVisible: true,
        itemOptionDuplicateLineVisible: true,
      },
      mobile: {
        createBasketVisible: true,
        downloadUploadExcelVisible: true,
        shipmentAddressVisible: true,
        breakValueVisible: true,
        deliveryDateVisible: true,
        confirmationDateVisible: true,
        contactSupportVisible: true,
        basketSummaryVisible: true,
        orderInformationVisible: true,
        transportNotificationBannerVisible: true,
        itemOptionMoveToAnotherBasketVisible: true,
        itemOptionScheduleDeliveryDateVisible: true,
        itemOptionDuplicateLineVisible: true,
      },
      contactSupportTitleMl: {},
      basketSummaryTitleMl: {},
      orderInformationFieldOrder: [
        'searchKey',
        'packedPerQuantity',
        'minOrderQuantity',
        'price',
        'totalDiscount',
        'priceAfterDiscount',
        'quantity',
        'unit',
        'grossAmount',
        'totalDiscountAmount',
        'lineTotal',
      ],
      orderInformationFields: [
        { key: 'searchKey', hidden: false, titleMl: {}, source: 'General_Information.searchKey1' },
        { key: 'packedPerQuantity', hidden: false, titleMl: {} },
        { key: 'minOrderQuantity', hidden: false, titleMl: {} },
        { key: 'price', hidden: false, titleMl: {} },
        { key: 'totalDiscount', hidden: false, titleMl: {} },
        { key: 'priceAfterDiscount', hidden: false, titleMl: {} },
        { key: 'quantity', hidden: false, titleMl: {} },
        { key: 'unit', hidden: false, titleMl: {} },
        { key: 'grossAmount', hidden: false, titleMl: {} },
        { key: 'totalDiscountAmount', hidden: false, titleMl: {} },
        { key: 'lineTotal', hidden: false, titleMl: {} },
      ],
      transportNotificationBannerTitleMl: {},
      transportNotificationBannerTextMl: {},
      transportNotificationBannerSymbolImage: '',
    };
  }
  if (componentKey === 'products') {
    const viewPorts = {
      verticalCard: true,
      horizontalCard: true,
      tableView: true,
      order: ['verticalCard', 'horizontalCard', 'tableView'],
      default: 'verticalCard',
    };
    return {
      pageName: {},
      pageNameMobile: {},
      banner: {
        visible: true,
        contentType: 'image',
        displayType: 'regular',
        scope: 'thisPage',
        selectedPageKeys: [],
        image: '',
        title: {},
        description: {},
        titleEnabled: true,
        descriptionEnabled: true,
        ctaEnabled: true,
        carouselSlides: [],
        deviceImageConfigured: true,
      },
      bannerMobile: {
        visible: true,
        contentType: 'image',
        displayType: 'regular',
        scope: 'thisPage',
        selectedPageKeys: [],
        image: '',
        title: {},
        description: {},
        titleEnabled: true,
        descriptionEnabled: true,
        ctaEnabled: true,
        carouselSlides: [],
        deviceImageConfigured: true,
      },
      exploreProducts: {
        visible: true,
        title: {},
      },
      exploreProductsMobile: {
        visible: true,
        title: {},
      },
      recentlyAdded: {
        visible: true,
        defaultOpen: false,
        title: {},
      },
      productsVisible: true,
      allProductsViewPorts: { ...viewPorts, order: [...viewPorts.order] },
      allProductsViewPortsMobile: { ...viewPorts, order: [...viewPorts.order] },
    };
  }
  if (componentKey === 'app-ad-dashbord') {
    return defaultCustomerDashboardPayload();
  }
  if (componentKey === 'dashboard') {
    return {
      kpis: {
        visible: true,
        items: [
          { key: 'kpi-1', title: {}, visible: true, symbol: 'symbol', icon: '' },
          { key: 'kpi-2', title: {}, visible: true, symbol: '', icon: '' },
          { key: 'kpi-3', title: {}, visible: true, symbol: '', icon: '' },
          { key: 'kpi-4', title: {}, visible: true, symbol: '', icon: '' },
        ],
      },
      orders: {
        visible: true,
        title: {},
        listSource: 'orders',
        itemsDisplayed: 3,
      },
      bestSeller: {
        visible: true,
        title: {},
        titleSource: 'custom',
        graphVisible: true,
        productVisible: true,
      },
      activeBps: {
        visible: true,
        title: {},
        titleSource: 'custom',
      },
      activeContacts: {
        visible: true,
        title: {},
        titleSource: 'custom',
      },
      recentBps: {
        title: {},
        itemsDisplayed: 3,
      },
      reorder: {
        title: {},
        itemsDisplayed: 3,
      },
      tickets: {
        enabled: true,
      },
      accountRequest: {
        enabled: true,
      },
      visitorsThisMonth: {
        enabled: true,
      },
    };
  }
  return {};
}

/** Default Flexible Template payload for customer dashboard layouts. */
export function defaultCustomerDashboardPayload(): Record<string, unknown> {
  const defaultBanner = {
    ...DEFAULT_PRODUCTS_PAGE_BANNER,
    titleEnabled: true,
    descriptionEnabled: true,
    ctaEnabled: true,
  };
  const defaultBannerFields = customerDashboardBannerFieldsFromConfig(
    defaultBanner,
    parseCustomerDashboardBannerModeDrafts({}, 'desktop', defaultBanner),
  );
  const dashboardRoot = {
    visible: true,
    ...defaultBannerFields,
    kpis: {
      order: ['orders', 'shipments', 'newItems'],
      orders: {
        titleMl: {},
        titleSource: 'CFDashboard.AllOrders',
      },
      shipments: {
        titleMl: {},
        titleSource: 'ADCustomerCustomize.totalShipments',
      },
      newItems: {
        titleMl: {},
        titleSource: 'CFDashboard.NewProducts',
        ctaEnabled: true,
      },
    },
    thisMonthOrder: {
      titleMl: {},
      filterType: 'orders',
      itemsDisplayed: 6,
    },
    calendar: {
      titleMl: {},
    },
    items: {
      titleMl: {},
      itemsViewPorts: {
        order: ['verticalCard', 'horizontalCard', 'tableView'],
        default: 'verticalCard',
        verticalCard: true,
        horizontalCard: true,
        tableView: true,
      },
    },
  };
  return {
    customerDashboard: { ...dashboardRoot },
    customerDashboardMobile: JSON.parse(JSON.stringify(dashboardRoot)),
  };
}
